import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { collection, query, where, getDocs, doc, getDoc, limit, startAfter } from 'firebase/firestore';
import { db } from '../api/firebase';
import {
  Typography,
  CircularProgress,
  Alert,
  Paper,
  TextField,
  Grid,
  Box,
  Breadcrumbs,
  Chip,
  Button,
  Container,
  InputAdornment,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon,
  School as SchoolIcon,
  PersonAdd as PersonAddIcon,
} from '@mui/icons-material';
import ProfessorListItem from '../components/ProfessorListItem';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';

const DepartmentPage = () => {
  const { universityId, departmentId } = useParams();
  const [department, setDepartment] = useState(null);
  const [university, setUniversity] = useState(null);
  const [lecturers, setLecturers] = useState([]);
  const [lastVisible, setLastVisible] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [hasMoreLecturers, setHasMoreLecturers] = useState(true);
  const { t, isArabic } = useLanguage();
  const { isAdmin } = useAuth();

  const LECTURERS_PER_PAGE = 9;
  const MAX_SEARCH_RESULTS = 6;

  const getDepartmentName = (englishName) => {
    if (!isArabic) return englishName;
    const translations = {
      'Accounting': 'المحاسبة', 'Aviation Department': 'قسم الطيران',
      'Finance': 'المالية', 'Management': 'الإدارة', 'Marketing': 'التسويق',
      'Interior Design': 'التصميم الداخلي', 'Computer Science': 'علوم الحاسب',
      'Information Systems': 'نظم المعلومات', 'Software Engineering': 'هندسة البرمجيات',
      'Communications and Networks Engineering': 'هندسة الاتصالات والشبكات',
      'Construction Engineering': 'هندسة الإنشاءات',
      'Production and Manufacture Engineering': 'هندسة الإنتاج والتصنيع',
      'General Studies': 'الدراسات العامة', 'Linguistics and Translation': 'اللغويات والترجمة',
      'Mathematics and Sciences': 'الرياضيات والعلوم', 'Law': 'القانون',
      'Architecture': 'العمارة', 'Preparatory Year Program': 'برنامج السنة التحضيرية',
    };
    return translations[englishName] || englishName;
  };

  useEffect(() => {
    const fetchInitialData = async () => {
      if (!universityId || !departmentId) return;

      setLoading(true);
      setError(null);
      try {
        const universityDocRef = doc(db, 'universities', universityId);
        const universitySnap = await getDoc(universityDocRef);
        if (universitySnap.exists()) {
          setUniversity({ id: universitySnap.id, ...universitySnap.data() });
        }

        const departmentDocRef = doc(db, 'departments', departmentId);
        const departmentSnap = await getDoc(departmentDocRef);
        if (departmentSnap.exists()) {
          setDepartment({ id: departmentSnap.id, ...departmentSnap.data() });
        }

        // Fetch first page of lecturers (removed separate count query to reduce reads)
        const lecturersQuery = query(
          collection(db, 'professors'),
          where('departmentId', '==', departmentId),
          limit(LECTURERS_PER_PAGE)
        );
        const profSnapshot = await getDocs(lecturersQuery);
        const profsList = profSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data(), _doc: doc }));
        
        setLecturers(profsList);
        
        if (profSnapshot.docs.length > 0) {
          setLastVisible(profSnapshot.docs[profSnapshot.docs.length - 1]);
          setHasMoreLecturers(profSnapshot.docs.length === LECTURERS_PER_PAGE);
        } else {
          setHasMoreLecturers(false);
        }

      } catch (err) {
        setError('Failed to load department data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, [universityId, departmentId]);

  const loadMoreLecturers = async () => {
    if (!lastVisible || loadingMore) return;

    setLoadingMore(true);
    try {
      const nextQuery = query(
        collection(db, 'professors'),
        where('departmentId', '==', departmentId),
        startAfter(lastVisible),
        limit(LECTURERS_PER_PAGE)
      );
      
      const nextSnapshot = await getDocs(nextQuery);
      const nextProfsList = nextSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data(), _doc: doc }));

      setLecturers(prev => [...prev, ...nextProfsList]);
      
      if (nextSnapshot.docs.length > 0) {
        setLastVisible(nextSnapshot.docs[nextSnapshot.docs.length - 1]);
        setHasMoreLecturers(nextSnapshot.docs.length === LECTURERS_PER_PAGE);
      } else {
        setHasMoreLecturers(false);
      }

    } catch (err) {
      setError('Failed to load more lecturers. Please try again.');
    } finally {
      setLoadingMore(false);
    }
  };

  const filteredLecturers = useMemo(() => {
    if (!searchQuery || searchQuery.length < 2) return lecturers;
    const lowerCaseQuery = searchQuery.toLowerCase();
    const filtered = lecturers.filter(prof => {
      const profName = prof?.name || prof?.full_name || '';
      return profName.toLowerCase().includes(lowerCaseQuery);
    });
    return filtered.slice(0, MAX_SEARCH_RESULTS);
  }, [lecturers, searchQuery]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress size={48} />
      </Box>
    );
  }

  if (error) {
    return <Container maxWidth="lg" sx={{ py: 4 }}><Alert severity="error">{error}</Alert></Container>;
  }

  const departmentDisplayName = department?.name ? getDepartmentName(department.name) : t('Department', 'القسم');

  return (
    <Box sx={{ direction: isArabic ? 'rtl' : 'ltr' }}>
      {/* Header */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
          py: { xs: 4, md: 5 },
          mb: 0,
        }}
      >
        <Container maxWidth="lg">
          {/* Breadcrumbs */}
          <Breadcrumbs sx={{ mb: 2, '& .MuiBreadcrumbs-separator': { color: 'rgba(255,255,255,0.5)' } }}>
            <Link to="/" style={{ textDecoration: 'none' }}>
              <Typography sx={{ color: 'rgba(255,255,255,0.8)', '&:hover': { color: 'white' } }}>
                {t('Home', 'الرئيسية')}
              </Typography>
            </Link>
            <Link to={`/university/${universityId}`} style={{ textDecoration: 'none' }}>
              <Typography sx={{ color: 'rgba(255,255,255,0.8)', '&:hover': { color: 'white' } }}>
                {university?.name || t('University', 'الجامعة')}
              </Typography>
            </Link>
            <Typography sx={{ color: 'white' }}>{departmentDisplayName}</Typography>
          </Breadcrumbs>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: 2,
                bgcolor: 'rgba(255,255,255,0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <SchoolIcon sx={{ color: 'white', fontSize: 32 }} />
            </Box>
            <Box>
              <Typography
                variant="h4"
                sx={{ color: 'white', fontWeight: 700, fontSize: { xs: '1.5rem', md: '2rem' } }}
              >
                {departmentDisplayName}
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.8)' }}>
                {lecturers.length}{hasMoreLecturers ? '+' : ''} {t('lecturers', 'محاضر')}
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ mt: -2, position: 'relative', zIndex: 3 }}>
        {/* Search Card */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 4,
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'grey.200',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          }}
        >
          <TextField
            placeholder={t('Search lecturers...', 'ابحث عن محاضر...')}
            variant="outlined"
            fullWidth
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'grey.400' }} />
                </InputAdornment>
              ),
              sx: {
                borderRadius: 3,
                bgcolor: 'grey.50',
                '& fieldset': { border: 'none' },
              }
            }}
          />
          {searchQuery.length >= 2 && hasMoreLecturers && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
              {t('Searching in loaded lecturers only. Load more to expand search.', 'البحث في المحاضرين المحملين فقط. حمّل المزيد لتوسيع البحث.')}
            </Typography>
          )}
        </Paper>

        {/* Lecturers Section */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'grey.200',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              {t('Lecturers', 'المحاضرين')}
              {searchQuery.length >= 2 && (
                <Chip
                  label={`${filteredLecturers.length} ${t('found', 'موجود')}`}
                  size="small"
                  sx={{ ml: 2 }}
                />
              )}
            </Typography>
            {isAdmin && (
              <Button
                component={Link}
                to="/add-lecturer"
                variant="outlined"
                size="small"
                startIcon={<PersonAddIcon />}
                sx={{ borderRadius: 2, textTransform: 'none' }}
              >
                {t('Add Lecturer', 'أضف محاضر')}
              </Button>
            )}
          </Box>
          
          {filteredLecturers.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <SchoolIcon sx={{ fontSize: 64, color: 'grey.300', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                {searchQuery.length >= 2
                  ? t('No lecturers found', 'لا يوجد محاضرين')
                  : t('No lecturers yet', 'لا يوجد محاضرين بعد')}
              </Typography>
              {isAdmin && (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    {t("Know a lecturer in this department?", "تعرف محاضر في هذا القسم؟")}
                  </Typography>
                  <Button
                    component={Link}
                    to="/add-lecturer"
                    variant="contained"
                    startIcon={<PersonAddIcon />}
                    sx={{ borderRadius: 3, textTransform: 'none' }}
                  >
                    {t("Add them to Me'ar", "أضفهم إلى معيار")}
                  </Button>
                </>
              )}
            </Box>
          ) : (
            <>
              <Grid container spacing={3}>
                {filteredLecturers.map((prof) => (
                  <Grid item xs={12} sm={6} md={4} key={prof.id}>
                    <ProfessorListItem lecturer={prof} departmentName={departmentDisplayName} />
                  </Grid>
                ))}
              </Grid>
              
              {(!searchQuery || searchQuery.length < 2) && hasMoreLecturers && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={loadMoreLecturers}
                    disabled={loadingMore}
                    startIcon={loadingMore ? <CircularProgress size={20} /> : <ExpandMoreIcon />}
                    sx={{
                      px: 5,
                      py: 1.5,
                      borderRadius: 3,
                      textTransform: 'none',
                      fontWeight: 500,
                    }}
                  >
                    {loadingMore 
                      ? t('Loading...', 'جاري التحميل...') 
                      : t('Load More', 'تحميل المزيد')}
                  </Button>
                </Box>
              )}
              
              {(!searchQuery || searchQuery.length < 2) && !hasMoreLecturers && lecturers.length > 0 && (
                <Box sx={{ textAlign: 'center', mt: 4, py: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('All lecturers loaded', 'تم تحميل جميع المحاضرين')}
                  </Typography>
                </Box>
              )}
            </>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default DepartmentPage;
